import React, { useState, useEffect } from "react";
import { Tag } from "@ann/web-components";
import { ActivityLogContainer } from "./ActivityLog.styled";
import { ActivityLogTableSection } from "./components";
import { getActivityLog } from "../../../../services";
import { useDebounce } from "../../../../../../../hooks";
import dayjs from "dayjs";

const ActivityLog = ({ workspaceId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("timestamp");
  const [orderDirection, setOrderDirection] = useState("DESC");
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [meta, setMeta] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 0,
    perPage: 0,
  });

  // Filter states
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedActions, setSelectedActions] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedModified, setSelectedModified] = useState("");
  
  // Debounce search keyword
  const debouncedSearchKeyword = useDebounce(searchKeyword, 500);

  // Fetch activity log from API
  useEffect(() => {
    const fetchActivityLog = async () => {
      if (!workspaceId) return;
      
      setLoading(true);
      try {
        const response = await getActivityLog({
          page: currentPage,
          page_size: itemsPerPage,
          order_by: orderBy,
          order_direction: orderDirection,
          search: debouncedSearchKeyword,
          roles: selectedRoles,
          actions: selectedActions,
          sections: selectedSections,
          modified: selectedModified,
        });

        if (response && response.body) {
          setActivities(response.body);
          if (response.meta) {
            setMeta({
              totalItems: response.meta.totalItems || 0,
              totalPages: response.meta.totalPages || 0,
              currentPage: response.meta.currentPage || currentPage,
              perPage: response.meta.perPage || itemsPerPage,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching activity log:", error);
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchActivityLog();
  }, [currentPage, itemsPerPage, orderBy, orderDirection, debouncedSearchKeyword, selectedRoles, selectedActions, selectedSections, selectedModified, workspaceId]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleSortChange = (selectedOption) => {
    const validOrderBy = ["timestamp", "userName", "action", "section", "outcome"];
    const newOrderBy = validOrderBy.includes(selectedOption) ? selectedOption : "timestamp";
    setOrderBy(newOrderBy);
    setCurrentPage(1);
  };

  const handleOrderChange = (currentOrder) => {
    const newOrder = currentOrder === 'asc' ? 'DESC' : 'ASC';
    setOrderDirection(newOrder);
    setCurrentPage(1);
  };

  const handleSearchChange = (keyword) => {
    setSearchKeyword(keyword);
    setCurrentPage(1);
  };

  const handleRoleChange = (role) => {
    setSelectedRoles(role ? [role] : []);
    setCurrentPage(1);
  };

  const handleActionChange = (action) => {
    setSelectedActions(action ? [action] : []);
    setCurrentPage(1);
  };

  const handleSectionChange = (section) => {
    setSelectedSections(section ? [section] : []);
    setCurrentPage(1);
  };

  const handleModifiedChange = (modified) => {
    setSelectedModified(modified || "");
    setCurrentPage(1);
  };


  const getOutcomeColor = (outcome) => {
    const normalizedOutcome = typeof outcome === 'string' ? outcome.toUpperCase() : '';
    
    switch (normalizedOutcome) {
      case 'SUCCESS':
        return 'success';
      case 'PENDING':
        return 'alert';
      case 'FAILED':
        return 'error';
      default:
        return 'neutral';
    }
  };

  const formatTimestamp = (dateString) => {
    if (!dateString) return "—";
    return dayjs(dateString).format("D MMM, YYYY · h:mm A");
  };

  // Map API response to table data
  const tableData = activities.map((activity) => {
    const outcomeText = activity.outcome || activity.outcome_status;
    const outcomeDisplay = outcomeText 
      ? outcomeText.charAt(0).toUpperCase() + outcomeText.slice(1).toLowerCase() 
      : "—";

    return {
      id:
        activity.activityUuid ||
        activity.id ||
        activity.created_at ||
        activity.timestamp,
      userName: activity.userName || activity.user_full_name || activity.userFullName || "—",
      action: activity.action || "—",
      section: activity.section || "—",
      item: activity.item || "—",
      detail: activity.detail || activity.description || "—",
      outcome: (
        <Tag
          color={getOutcomeColor(outcomeText)}
          size="small"
          text={outcomeDisplay}
        />
      ),
      timestamp: formatTimestamp(activity.timestamp || activity.created_at || activity.createdAt),
    };
  });

  const headers = [
    { label: 'User Name', value: 'userName' },
    { label: 'Action', value: 'action' },
    { label: 'Section', value: 'section' },
    { label: 'Item', value: 'item' },
    { label: 'Detail', value: 'detail' },
    { label: 'Outcome', value: 'outcome' },
    { label: 'Timestamp', value: 'timestamp' }
  ];

  const getSelectedSortOption = () => {
    const validOrderBy = ["timestamp", "userName", "action", "section", "outcome"];
    return validOrderBy.includes(orderBy) ? orderBy : "timestamp";
  };

  const getOrderDirection = () => {
    return orderDirection.toLowerCase();
  };

  return (
    <ActivityLogContainer>
      <ActivityLogTableSection
        tableData={tableData}
        headers={headers}
        currentPage={meta.currentPage || currentPage}
        itemsPerPage={meta.perPage || itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onPageChange={handlePageChange}
        totalItems={meta.totalItems || 0}
        totalPages={meta.totalPages || 0}
        loading={loading}
        onSortChange={handleSortChange}
        onOrderChange={handleOrderChange}
        selectedSortOption={getSelectedSortOption()}
        orderDirection={getOrderDirection()}
        onSearchChange={handleSearchChange}
        onRoleChange={handleRoleChange}
        onActionChange={handleActionChange}
        onSectionChange={handleSectionChange}
        onModifiedChange={handleModifiedChange}
      />
    </ActivityLogContainer>
  );
};

export default ActivityLog;
